import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { PaginatedResponse } from '../models/paginatedResponse';
import { FamilyStatusCheckSearchRequest } from '../models/api/familyStatusCheckSearchRequest';
import { FamilyStatusResultDto } from '../models/api/familyStatusResultDto';
import { FamilyStatusProblemTypeDto } from '../models/api/familyStatusProblemTypeDto';
import { FamilyStatusProblemType } from '../models/enums/familyStatusProblemType.enum';
import { Capitalization } from '../models/enums/capitalization.enum';

@Injectable({
  providedIn: 'root'
})
export class FamilyStatusCheckService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/tools/family-status-check`;

  /**
   * GET /api/tools/family-status-check/problem-types
   * Lists the available family-status problem types.
   */
  getProblemTypes(): Observable<FamilyStatusProblemTypeDto[]> {
    return this.http.get<FamilyStatusProblemTypeDto[]>(`${this.apiUrl}/problem-types`);
  }

  /**
   * GET /api/tools/family-status-check/search
   * Runs a family-status check and returns paginated results.
   */
  search(request: FamilyStatusCheckSearchRequest): Observable<PaginatedResponse<FamilyStatusResultDto>> {
    return this.http.get<PaginatedResponse<FamilyStatusResultDto>>(`${this.apiUrl}/search`, {
      params: this.buildParams(request)
    });
  }

  /**
   * GET /api/tools/family-status-check/export
   * Exports one family-status check's full result set as CSV.
   */
  exportCsv(request: FamilyStatusCheckSearchRequest): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.apiUrl}/export`, {
      params: this.buildParams(request),
      responseType: 'blob',
      observe: 'response'
    });
  }

  /**
   * GET /api/tools/family-status-check/export-all
   * Exports every family-status check, unioned, as a single CSV.
   */
  exportAllCsv(countryId?: string, year?: number, capitalization?: Capitalization): Observable<HttpResponse<Blob>> {
    let params = new HttpParams();
    if (countryId) {
      params = params.set('countryId', countryId);
    }
    if (year !== undefined && year !== null) {
      params = params.set('year', year.toString());
    }
    if (capitalization !== undefined && capitalization !== null) {
      params = params.set('capitalization', capitalization.toString());
    }

    return this.http.get(`${this.apiUrl}/export-all`, {
      params,
      responseType: 'blob',
      observe: 'response'
    });
  }

  /**
   * PUT /api/tools/family-status-check/reviews
   * Marks a reported director as confirmed correct at a company — in every year, so later runs
   * leave them out — or clears it.
   */
  setReviewed(
    problemType: FamilyStatusProblemType,
    holderId: string,
    companyId: string,
    reviewed: boolean
  ): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/reviews`, { problemType, holderId, companyId, reviewed });
  }

  private buildParams(request: FamilyStatusCheckSearchRequest): HttpParams {
    let params = new HttpParams().set('problemType', request.problemType.toString());

    if (request.countryId) {
      params = params.set('countryId', request.countryId);
    }
    if (request.year !== undefined && request.year !== null) {
      params = params.set('year', request.year.toString());
    }
    if (request.capitalization !== undefined && request.capitalization !== null) {
      params = params.set('capitalization', request.capitalization.toString());
    }
    if (request.hideReviewed) {
      params = params.set('hideReviewed', 'true');
    }
    if (request.pageNumber !== undefined && request.pageNumber !== null) {
      params = params.set('pageNumber', request.pageNumber.toString());
    }
    if (request.pageSize !== undefined && request.pageSize !== null) {
      params = params.set('pageSize', request.pageSize.toString());
    }
    if (request.sortBy) {
      params = params.set('sortBy', request.sortBy);
    }
    if (request.ascending !== undefined && request.ascending !== null) {
      params = params.set('ascending', request.ascending.toString());
    }

    return params;
  }
}
